// The pinned transparency head: the browser twin of the Windows keystore's
// TranslogHead (the vendored shared/translog.js defaultHeadStore is NOT used
// here). Persisted per crypto accountId in localStorage as
//   { seq, hash, signerPk, genesisHash }
// — the chain head a successful context build verified (Decryptor.js) plus
// the hash of that chain's genesis entry (2026-10-13; a pin saved earlier
// lacks it until its next re-pin). Kept apart from Decryptor.js, which pulls
// in the store, so the generation rule below is unit-testable.

export const headStore = {
  key: (accountId) => `nc1.translogHead.${accountId}`,
  load (accountId) {
    try { return JSON.parse(localStorage.getItem(this.key(accountId))) } catch { return null }
  },
  save (accountId, head) {
    try { localStorage.setItem(this.key(accountId), JSON.stringify(head)) } catch { /* private mode */ }
  },
  forget (accountId) {
    try { localStorage.removeItem(this.key(accountId)) } catch { /* storage unavailable */ }
  },
}

/**
 * May an alarm against this pin be forgiven at all? Only when the served
 * chain descends from a DIFFERENT genesis than the pin was taken under: an
 * owner reset is a new genesis (OWNER-RESET.md), while the same genesis is
 * the same account history, so a shorter or divergent chain of it is the
 * rollback the pin exists to catch, whatever the server declares. The server
 * can neither sign a same-genesis chain the owner did not, nor make a
 * different-genesis chain that is a prefix of the real one (libsodium audit
 * finding 4, 2026-10-13). A pin saved before the genesis was recorded cannot
 * tell: it is eligible once, and the re-pin after that carries the genesis.
 */
export function isOtherGeneration (pinned, servedGenesisHash) {
  if (!pinned) return false
  if (!pinned.genesisHash) return true
  return pinned.genesisHash !== servedGenesisHash
}

/**
 * A complete head as this module pins it: { seq, hash, signerPk, genesisHash }.
 * Used where a head arrives from elsewhere (the one an approving browser
 * seals into a device-link grant, DeviceLink.js) and must be checked before
 * it is trusted as a continuity checkpoint.
 */
export function isCompleteHead (head) {
  return !!head && typeof head === 'object' &&
    Number.isInteger(head.seq) && head.seq >= 0 &&
    typeof head.hash === 'string' && head.hash.length > 0 &&
    typeof head.signerPk === 'string' && head.signerPk.length > 0 &&
    typeof head.genesisHash === 'string' && head.genesisHash.length > 0
}
